import type { UseQueryOptions } from "@pinia/colada";
import type { ObjectToCamel } from "ts-case-convert/lib/caseConvert";

import { objectToCamel } from "ts-case-convert";
import { toValue } from "vue";

import type { ProductCms } from "@solvari/statamic";
import type { LocaleMorpheus } from "@solvari/translations";

import { toSearchParams } from "@solvari/utils";

import type { NarrowedCmsResponse } from "~/shared/libs/narrowedCmsResponse.ts";

import { kyCacheTags } from "~/shared/composables/useCacheTags.ts";
import { cmsCollectionEntriesUrl } from "~/shared/composables/useCmsApi.ts";
import { useStatamicPreview } from "~/shared/composables/useStatamicPreview.ts";
import { handleNuxtQuery } from "~/shared/libs/networkErrorHandling.ts";

type CmsLocalSearch = ObjectToCamel<ProductCms["local_search"]>;

// Pinned in tests/features/cmsNarrowedFields.spec.ts. No `uri`: URLs derive from geo slugs.
const localSearchFields = ["id", "local_search"] as const;

type LocalSearchResponse = NarrowedCmsResponse<
  ProductCms,
  typeof localSearchFields
>;

function useCmsLocalSearch(params: { locale: LocaleMorpheus; uri: string }) {
  const cmsUrl = cmsCollectionEntriesUrl("pages");

  const { previewToken, previewSite } = useStatamicPreview();

  return {
    key: () => ["cms", "local-search", params, toValue(previewToken)],
    query: handleNuxtQuery(async (): Promise<CmsLocalSearch | null> => {
      const response = objectToCamel(
        await kyCacheTags
          .get(cmsUrl, {
            searchParams: toSearchParams(
              {
                fields: localSearchFields.join(","),
                filter: {
                  blueprint: "product",
                  site: previewSite.value ?? params.locale,
                  uri: params.uri,
                },
                limit: 1,
                token: previewToken.value,
              },
              { nestedKeys: true },
            ),
          })
          .json<LocalSearchResponse>(),
      );
      return response.data[0]?.localSearch ?? null;
    }),
    staleTime: 5 * 60 * 1000,
  } satisfies UseQueryOptions;
}

export type { CmsLocalSearch };
export { localSearchFields, useCmsLocalSearch };
